import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { CalendarRange } from 'lucide-react';
import { DetailDrawer, ErrorState, money, fmtDate } from './design';

// Set up a quarter for every Support at Home participant at once.
//
// Services Australia sets each participant's budget for every quarter. Software connected to
// Services Australia (Brevity's "SAH Sync") pulls them in; until Rostery is certified for that
// connection, this is the next best thing: one screen listing everyone for the quarter, with the
// amount filled in from their classification's quarterly rate (Rates and fees) or last quarter's,
// and carryover worked out on save. Saving also re-checks their booked visits, so a visit held
// back for want of a budget goes live.

const pad = (n) => String(n).padStart(2, '0');
function quarterOptions() {
  const d = new Date();
  const qm = Math.floor(d.getMonth() / 3) * 3; // 0-based month of the quarter's start
  const NAMES = ['January to March', 'April to June', 'July to September', 'October to December'];
  const out = [];
  for (let k = 0; k < 2; k++) {
    const start = new Date(d.getFullYear(), qm + k * 3, 1);
    out.push({ value: `${start.getFullYear()}-${pad(start.getMonth() + 1)}-01`, label: `${NAMES[start.getMonth() / 3]} ${start.getFullYear()}${k === 0 ? ' (this quarter)' : ' (next quarter)'}` });
  }
  return out;
}

export default function QuarterSetup({ open, onClose, onDone }) {
  const options = useMemo(quarterOptions, []);
  const [quarter, setQuarter] = useState(options[0].value);
  const [data, setData] = useState(null);
  const [amounts, setAmounts] = useState({});
  const [loading, setLoading] = useState(false);
  // Kept on screen rather than toasted: an empty list after a failed load would read as "no
  // participants to set up".
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setData(null); setError(null);
    try {
      const r = await api.get('/aged-care/budgets/quarter-setup', { params: { quarter_start: quarter } });
      setData(r.data);
      const a = {};
      (r.data.rows || []).forEach((x) => { if (!x.existing && x.suggested != null) a[x.clientId] = String(x.suggested); });
      setAmounts(a);
    } catch (e) { setError(e.response?.data?.message || 'Could not load the quarter'); }
    finally { setLoading(false); }
  }, [quarter]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const rows = (data && data.rows) || [];
  const todo = rows.filter((r) => !r.existing);
  const ready = todo.filter((r) => Number(amounts[r.clientId]) > 0);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.post('/aged-care/budgets/quarter-setup', {
        quarter_start: quarter,
        rows: ready.map((x) => ({ clientId: x.clientId, amount: Number(amounts[x.clientId]) })),
      });
      const v = r.data.visits || {};
      toast.success(`${r.data.created} budget${r.data.created === 1 ? '' : 's'} created${v.released ? `. ${v.released} held visit${v.released === 1 ? ' is' : 's are'} live again` : ''}${v.held ? `. ${v.held} visit${v.held === 1 ? '' : 's'} still held` : ''}.`);
      onDone?.(); onClose?.();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not create the budgets'); }
    finally { setSaving(false); }
  };

  return (
    <DetailDrawer
      open={open} onClose={onClose} width="lg" icon={CalendarRange}
      title="Set up a quarter"
      subtitle="Every Support at Home participant's budget for the quarter, in one go."
      footer={<>
        <button type="button" onClick={onClose} className="btn-secondary text-sm">Cancel</button>
        <button type="button" onClick={save} disabled={saving || !ready.length} className="btn-primary text-sm">
          {saving ? 'Creating…' : `Create ${ready.length} budget${ready.length === 1 ? '' : 's'}`}
        </button>
      </>}
    >
      <div className="space-y-4">
        <label className="block">
          <span className="block text-[13px] font-medium text-slate-700 mb-1">Quarter</span>
          <select value={quarter} onChange={(e) => setQuarter(e.target.value)}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
            {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
        <p className="text-[13px] text-slate-600">
          Enter each budget from Services Australia (the Aged Care Provider Portal shows it). The amount is filled in from the classification's quarterly rate in Rates and fees, or last quarter's. 10% goes to the care management account, and carryover from last quarter is added when you save.
        </p>

        {loading ? (
          <p className="text-[13px] text-slate-500 py-6 text-center">Loading the participants…</p>
        ) : error ? (
          <ErrorState message={error} onRetry={load} />
        ) : !rows.length ? (
          <p className="text-[13px] text-slate-500 py-6 text-center">No Support at Home participants with a classification.</p>
        ) : (
          <div className="rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full text-[13px]">
              <thead className="bg-slate-50 text-slate-600 text-left">
                <tr>
                  <th className="px-3 py-2 font-medium">Participant</th>
                  <th className="px-3 py-2 font-medium">Quarterly budget</th>
                  <th className="px-3 py-2 font-medium text-right">Carryover in</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((x) => (
                  <tr key={x.clientId}>
                    <td className="px-3 py-2">
                      <div className="font-medium text-slate-900">{x.name}</div>
                      <div className="text-[12px] text-slate-500">Classification {x.level}{x.saId ? '' : ', no Services Australia ID'}</div>
                    </td>
                    <td className="px-3 py-2">
                      {x.existing ? (
                        <span className="text-slate-600">Already set: {money(x.existing.amount)}</span>
                      ) : (
                        <div>
                          <div className="flex items-center gap-1">
                            <span className="text-slate-500">$</span>
                            <input
                              type="number" min="0" step="0.01" inputMode="decimal" aria-label={`Quarterly budget for ${x.name}`}
                              value={amounts[x.clientId] ?? ''} onChange={(e) => setAmounts((p) => ({ ...p, [x.clientId]: e.target.value }))}
                              className="w-32 border border-slate-200 rounded-lg px-2 py-1.5 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-primary-500"
                            />
                          </div>
                          {x.suggestedFrom && <div className="text-[11px] text-slate-500 mt-0.5">From {x.suggestedFrom === 'classification' ? 'your classification rate' : x.suggestedFrom === 'government figures' ? 'the government budget for the classification' : "last quarter's budget"}</div>}
                          {Array.isArray(x.notes) && x.notes.length > 0 && <div className="text-[11px] text-slate-500">{x.notes.join(' · ')}</div>}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-600">
                      {x.existing ? money(x.existing.carryover) : x.carryover != null ? money(x.carryover) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {data && data.quarter && (
          <p className="text-[12px] text-slate-500">{fmtDate(data.quarter.start)} to {fmtDate(data.quarter.end)}. Saving checks every booked visit in the quarter against the new budgets.</p>
        )}
      </div>
    </DetailDrawer>
  );
}
